import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { ContractStatusNotification as ContractStatusNotificationType } from "@/gql/graphql";
import { ContractStatusType, ContractStatusReason } from "@/gql/graphql";
import { DATE_FORMAT } from "@/constants";
import DataListItemText from "@/components/app/Navigation/GlobalHeader/Notifications/Data/DataListItemText";

type NotificationProps = {
  data: Pick<
    ContractStatusNotificationType,
    | "activationDate"
    | "amendmentQuoteId"
    | "contractNumber"
    | "customerName"
    | "status"
    | "statusChangeReason"
  >;
};

const ContractStatusNotification = (props: NotificationProps) => {
  const { data } = props;
  const {
    contractNumber,
    customerName,
    status,
    statusChangeReason,
    activationDate,
    amendmentQuoteId,
  } = data;
  const { t } = useTranslation();

  const activationDateFormat = activationDate
    ? dayjs(activationDate).format(DATE_FORMAT.DATE)
    : undefined;

  if (
    status === ContractStatusType.AmendmentDraft ||
    status === ContractStatusType.Draft ||
    status === ContractStatusType.Expired
  ) {
    return;
  }

  let statusKey;
  let titleKey: string;

  const isAmendmentCancelled =
    statusChangeReason === ContractStatusReason.PriceChangeRejected ||
    (statusChangeReason === ContractStatusReason.DCountryFormRequestRejected &&
      amendmentQuoteId != null);

  if (isAmendmentCancelled) {
    statusKey = "amendment_cancelled";
    titleKey = `notifications.contract_status.${statusKey}.title`;
  } else if (
    status === ContractStatusType.Cancelled &&
    statusChangeReason === ContractStatusReason.DCountryFormRequestRejected
  ) {
    statusKey = "cancelled.d_country_form_request_rejected";
    titleKey = `notifications.contract_status.${statusKey}.title`;
  } else if (status === ContractStatusType.Cancelled && statusChangeReason) {
    statusKey = `cancelled.${statusChangeReason.toLowerCase()}` as const;
    titleKey = `notifications.contract_status.${statusKey}.title`;
  } else {
    statusKey = status.toLowerCase();
    titleKey = `notifications.contract_status.${statusKey}.title`;
  }

  if (!statusKey) {
    return;
  }

  let descriptionKey: string;
  if (isAmendmentCancelled) {
    descriptionKey = `notifications.contract_status.amendment_cancelled.description`;
  } else if (
    status === ContractStatusType.Cancelled &&
    statusChangeReason === ContractStatusReason.DCountryFormRequestRejected
  ) {
    descriptionKey = `notifications.contract_status.${statusKey}.description`;
  } else if (status === ContractStatusType.Cancelled && statusChangeReason) {
    descriptionKey = `notifications.contract_status.cancelled.${statusChangeReason.toLowerCase()}.description`;
  } else {
    descriptionKey = `notifications.contract_status.${statusKey}.description`;
  }

  return (
    <DataListItemText
      primary={t(titleKey, {
        contractNumber,
        customerName,
      })}
      secondary={t(descriptionKey, {
        contractNumber,
        activationDate: activationDateFormat,
      })}
    />
  );
};

export default ContractStatusNotification;
